// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckboxWithInput from "@store/Library/Components/Toolkit/CheckboxWithInput";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { CheckboxPresentationProps } from "@store/MobiusOverrides/Checkbox/Checkbox";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { formGridProps } from "@store/Themes/common/props/form";
import constants from "@store/constants";
import useFieldMask from "@store/Framework/Common/Hooks/useFieldMask";
import useIsCSDS from "@store/Framework/Common/Hooks/useIsCSDS";
import getPostalCodeConstants from "@store/Framework/Common/Utilities/getPostalCodeConstants";
import CheckboxFieldset, { CheckboxFieldsetPresentationProps } from "@store/Library/Components/CheckboxFieldset";
import useQueryString from "@toolkit/Framework/Common/Hooks/useQueryString";
import { useCurrentLanguage, useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useCurrentCountries } from "@toolkit/Framework/Store/Data/Countries/CountriesHooks";
import React, {
    ChangeEventHandler,
    createRef,
    FC,
    FormEventHandler,
    RefObject,
    useEffect,
    useRef,
    useState,
} from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch } from "react-redux";

const recaptchaLocation = "ContactUs";

const getFieldIdTooltip = (defaultValue: string) => `Default: "${defaultValue}"`;
const defaultFieldIds = {
    firstName: "first_name",
    lastName: "last_name",
    email: "email",
    company: "company",
    title: "00NHn00000PkbM9",
    phone: "phone",
    street: "street",
    city: "city",
    state: "state",
    zip: "zip",
    comment: "00N2S000007MqXj",
};

const enum fields {
    firstNameId = "firstNameId",
    lastNameId = "lastNameId",
    emailId = "emailId",
    companyId = "companyId",
    titleId = "titleId",
    phoneId = "phoneId",
    streetId = "streetId",
    cityId = "cityId",
    stateId = "stateId",
    zipId = "zipId",
    commentId = "commentId",
}

export interface ContactUsFormProps extends WidgetProps {
    fields: {
        [fields.firstNameId]?: string;
        [fields.lastNameId]?: string;
        [fields.emailId]?: string;
        [fields.companyId]?: string;
        [fields.titleId]?: string;
        [fields.phoneId]?: string;
        [fields.streetId]?: string;
        [fields.cityId]?: string;
        [fields.stateId]?: string;
        [fields.zipId]?: string;
        [fields.commentId]?: string;
    };
}

export interface ContactUsFormStyles {
    agreementMessage?: TypographyPresentationProps;
    checkbox?: CheckboxPresentationProps;
    checkboxFieldset?: CheckboxFieldsetPresentationProps;
    errorMessage?: InfosBoxProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    gridItemFull?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successMessage?: InfosBoxProps;
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
}

const contactUsFormStyles: ContactUsFormStyles = {
    successMessage: { type: "success" },
    errorMessage: { type: "error" },
    gridContainer: formGridProps.gridContainer,
    gridItem: { width: [12, 6, 6, 6, 6] },
    gridItemFull: { width: 12 },
    agreementMessage: { variant: "p" },
    submitButton: { variant: "primary" },
};

const styles = contactUsFormStyles;

const ContactUsForm: FC<ContactUsFormProps> = ({ fields }) => {
    const dispatch = useDispatch();
    const language = useCurrentLanguage()?.languageCode?.slice(0, 2).toUpperCase();
    const { success } = useQueryString();
    const formRef = useRef<HTMLFormElement>(null);
    const showErrorsRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const { salesforceOrganizationId, salesforceSubmitUrl } = useSettingsCollection().customSettings;
    const recaptchaRef: RefObject<ReCAPTCHA> = createRef();
    const countries = useCurrentCountries();

    useEffect(() => {
        if (!countries) {
            dispatch(loadCurrentCountries());
        }
    }, []);

    const isCSDS = useIsCSDS();
    const siteName = isCSDS ? "CSDS" : "Cansel";
    const recordType = isCSDS ? "0122S0000009FyR" : "0122S000000NRF4";
    const countryAbbreviation = isCSDS ? "US" : "CA";
    const currencyCode = isCSDS ? "USD" : "CAD";

    // --- First Name Field

    const firstNameConstants = constants.formField.salesforce.firstName;
    const firstNameId = fields.firstNameId || defaultFieldIds.firstName;

    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState("");

    const validateFirstName = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", firstNameConstants.label()).toString()
            : !firstNameConstants.regExp.test(value)
            ? firstNameConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setFirstNameError(msg);
        }

        return !msg;
    };

    const onChangeFirstName: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateFirstName(value);
        setFirstName(value);
    };

    // --- Last Name Field

    const lastNameConstants = constants.formField.salesforce.lastName;
    const lastNameId = fields.lastNameId || defaultFieldIds.lastName;

    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState("");

    const validateLastName = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", lastNameConstants.label()).toString()
            : !lastNameConstants.regExp.test(value)
            ? lastNameConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setLastNameError(msg);
        }

        return !msg;
    };

    const onChangeLastName: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateLastName(value);
        setLastName(value);
    };

    // --- Email Field

    const emailConstants = constants.formField.email;
    const emailId = fields.emailId || defaultFieldIds.email;

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");

    const validateEmail = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", emailConstants.label()).toString()
            : !emailConstants.regExp.test(value)
            ? emailConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setEmailError(msg);
        }

        return !msg;
    };

    const onChangeEmail: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateEmail(value);
        setEmail(value);
    };

    // --- Company Field

    const companyLabel = translate("Cansel_Form_Company");
    const companyId = fields.companyId || defaultFieldIds.company;

    const [company, setCompany] = useState("");
    const [companyError, setCompanyError] = useState("");

    const validateCompany = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", companyLabel).toString() : "";

        if (showErrorsRef.current) {
            setCompanyError(msg);
        }

        return !msg;
    };

    const onChangeCompany: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateCompany(value);
        setCompany(value);
    };

    // --- Title Field

    const titleLabel = translate(`Cansel_Form_Title`);
    const titlePlaceholder = translate(`Cansel_Form_Title_Placeholder`);
    const titleId = fields.titleId || defaultFieldIds.title;

    const [title, setTitle] = useState("");
    const [titleError, setTitleError] = useState("");

    const validateTitle = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", titleLabel).toString() : "";

        if (showErrorsRef.current) {
            setTitleError(msg);
        }

        return !msg;
    };

    const onChangeTitle: ChangeEventHandler<HTMLSelectElement> = event => {
        const value = event.currentTarget.value;
        validateTitle(value);
        setTitle(value);
    };

    // --- Phone Field

    const phoneConstants = constants.formField.phone;
    const phoneId = fields.phoneId || defaultFieldIds.phone;

    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");

    const phoneMask = useFieldMask(phoneConstants.maskOptions, phone, setPhone);

    const validatePhone = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", phoneConstants.label()).toString()
            : !phoneConstants.regExp.test(value)
            ? phoneConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setPhoneError(msg);
        }

        return !msg;
    };

    const onChangePhone: ChangeEventHandler<HTMLInputElement> = event => {
        phoneMask.resolve(event.currentTarget.value);
        validatePhone(phoneMask.value);
        setPhone(phoneMask.value);
    };

    // --- Street Field

    const streetConstants = constants.formField.salesforce.street;
    const streetId = fields.streetId || defaultFieldIds.street;

    const [street, setStreet] = useState("");
    const [streetError, setStreetError] = useState("");

    const validateStreet = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", streetConstants.label()).toString()
            : !streetConstants.regExp.test(value)
            ? streetConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setStreetError(msg);
        }

        return !msg;
    };

    const onChangeStreet: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateStreet(value);
        setStreet(value);
    };

    // --- City Field

    const cityConstants = constants.formField.salesforce.city;
    const cityId = fields.cityId || defaultFieldIds.city;

    const [city, setCity] = useState("");
    const [cityError, setCityError] = useState("");

    const validateCity = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", cityConstants.label()).toString()
            : !cityConstants.regExp.test(value)
            ? cityConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setCityError(msg);
        }

        return !msg;
    };

    const onChangeCity: ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        validateCity(value);
        setCity(value);
    };

    // --- State Field

    const stateTranslationKey = countryAbbreviation === "CA" ? "Province" : "State";
    const stateLabel = translate(`Cansel_Form_${stateTranslationKey}`);
    const statePlaceholder = translate(`Cansel_Form_${stateTranslationKey}_Placeholder`);
    const stateId = fields.stateId || defaultFieldIds.state;

    const [state, setState] = useState("");
    const [stateError, setStateError] = useState("");

    const validateState = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", stateLabel).toString() : "";

        if (showErrorsRef.current) {
            setStateError(msg);
        }

        return !msg;
    };

    const onChangeState: ChangeEventHandler<HTMLSelectElement> = event => {
        const value = event.currentTarget.value;
        validateState(value);
        setState(value);
    };

    // --- Postal Code Field

    const postalCodeConstants = getPostalCodeConstants(countryAbbreviation);
    const zipId = fields.zipId || defaultFieldIds.zip;

    const [postalCode, setPostalCode] = useState("");
    const [postalCodeError, setPostalCodeError] = useState("");

    const postalCodeMask = useFieldMask(postalCodeConstants.maskOptions, postalCode, setPostalCode);

    const validatePostalCode = (value: string) => {
        const msg = !value.trim()
            ? siteMessage("Field_Required", postalCodeConstants.label()).toString()
            : !postalCodeConstants.regExp.test(value)
            ? postalCodeConstants.invalidMessage()
            : "";

        if (showErrorsRef.current) {
            setPostalCodeError(msg);
        }

        return !msg;
    };

    const onChangePostalCode: ChangeEventHandler<HTMLInputElement> = event => {
        postalCodeMask.resolve(event.currentTarget.value);
        validatePostalCode(postalCodeMask.value);
        setPostalCode(postalCodeMask.value);
    };

    // --- Comment Field

    const [comment, setComment] = useState("");
    const commentId = fields.commentId || defaultFieldIds.comment;

    const onChangeComment: ChangeEventHandler<HTMLTextAreaElement> = event => {
        setComment(event.currentTarget.value);
    };

    // ---

    const submitHandler: FormEventHandler<HTMLFormElement> = event => {
        event.preventDefault();
        showErrorsRef.current = true;

        const isFirstNameValid = validateFirstName(firstName);
        const isLastNameValid = validateLastName(lastName);
        const isEmailValid = validateEmail(email);
        const isCompanyValid = validateCompany(company);
        const isTitleValid = validateTitle(title);
        const isPhoneValid = validatePhone(phone);
        const isStreetValid = validateStreet(street);
        const isCityValid = validateCity(city);
        const isStateValid = validateState(state);
        const isPostalCodeValid = validatePostalCode(postalCode);

        let isReCaptchaValid = false;
        dispatch(
            validateReCaptcha({
                location: recaptchaLocation,
                recaptchaRef,
                onSuccess: isValid => {
                    isReCaptchaValid = isValid;
                },
            }),
        );

        if (
            !isFirstNameValid ||
            !isLastNameValid ||
            !isEmailValid ||
            !isCompanyValid ||
            !isTitleValid ||
            !isPhoneValid ||
            !isStreetValid ||
            !isCityValid ||
            !isStateValid ||
            !isPostalCodeValid ||
            !isReCaptchaValid
        ) {
            return;
        }

        setIsLoading(true);
        formRef.current?.submit();
    };

    if (success === "true") {
        return <InfosBox {...styles.successMessage}>{siteMessage("Cansel_ContactUs_Success")}</InfosBox>;
    }

    if (!salesforceOrganizationId || !salesforceSubmitUrl) {
        return (
            <InfosBox {...styles.errorMessage}>{translate("An error has occurred, please try again later.")}</InfosBox>
        );
    }

    const states = countries?.find(x => x.abbreviation === countryAbbreviation)?.states;

    const returnUrl =
        typeof window !== "undefined" ? `${window.location.origin}${window.location.pathname}?success=true` : "";

    return (
        <StyledWrapper {...styles.wrapper}>
            <LoadingOverlay {...styles.loadingOverlay} loading={isLoading}>
                <form ref={formRef} action={salesforceSubmitUrl} method="POST" onSubmit={submitHandler} noValidate>
                    <input type="hidden" name="oid" value={salesforceOrganizationId} />
                    <input type="hidden" name="retURL" value={returnUrl} />
                    <input type="hidden" id="recordType" name="recordType" value={recordType} />
                    <input type="hidden" id="00NE0000006IWP2" name="00NE0000006IWP2" value={language} />
                    <input type="hidden" id="country" name="country" value={countryAbbreviation} />
                    <input type="hidden" id="currency" name="currency" value={currencyCode} />
                    <input type="hidden" id="lead_source" name="lead_source" value="Web" />
                    <input type="hidden" id="00N44000006vItL" name="00N44000006vItL" value={siteName} />
                    <GridContainer {...styles.gridContainer}>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={firstNameError}
                                id={firstNameId}
                                label={firstNameConstants.label()}
                                maxLength={19}
                                name={firstNameId}
                                onChange={onChangeFirstName}
                                placeholder={firstNameConstants.placeholder()}
                                required={true}
                                value={firstName}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={lastNameError}
                                id={lastNameId}
                                label={lastNameConstants.label()}
                                maxLength={20}
                                name={lastNameId}
                                onChange={onChangeLastName}
                                placeholder={lastNameConstants.placeholder()}
                                required={true}
                                value={lastName}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={emailError}
                                id={emailId}
                                label={emailConstants.label()}
                                maxLength={80}
                                name={emailId}
                                onChange={onChangeEmail}
                                placeholder={emailConstants.placeholder()}
                                required={true}
                                value={email}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={companyError}
                                id={companyId}
                                label={companyLabel}
                                maxLength={100}
                                name={companyId}
                                onChange={onChangeCompany}
                                required={true}
                                value={company}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Select
                                error={titleError}
                                id={titleId}
                                label={titleLabel}
                                name={titleId}
                                onChange={onChangeTitle}
                                required={true}
                                value={title}
                            >
                                <option value="">{titlePlaceholder}</option>
                                <option value="President / CEO / Owner / VP">
                                    {translate("Cansel_Form_Title_President/CEO/Owner/VP")}
                                </option>
                                <option value="Chief Financial Officer">
                                    {translate("Cansel_Form_Title_ChiefFinancialOfficer")}
                                </option>
                                <option value="Chief Operating Officer">
                                    {translate("Cansel_Form_Title_ChiefOperatingOfficer")}
                                </option>
                                <option value="Director / Manager / Project Manager">
                                    {translate("Cansel_Form_Title_Director/Manager/ProjectManager")}
                                </option>
                                <option value="Assistant / Coordinator">
                                    {translate("Cansel_Form_Title_Assistant/Coordinator")}
                                </option>
                                <option value="Purchaser">{translate("Cansel_Form_Title_Purchaser")}</option>
                                <option value="Teacher / Instructor">
                                    {translate("Cansel_Form_Title_Teacher/Instructor")}
                                </option>
                                <option value="Field technician (Survey)">
                                    {translate("Cansel_Form_Title_FieldTechnician")}
                                </option>
                                <option value="Office technician (CAD) / Analyst">
                                    {translate("Cansel_Form_Title_OfficeTechnician/Analyst")}
                                </option>
                                <option value="Bookkeeper / Account payable manager">
                                    {translate("Cansel_Form_Title_Bookkeeper/AccountPayableManager")}
                                </option>
                                <option value="Consultant">{translate("Cansel_Form_Title_Consultant")}</option>
                                <option value="Student">{translate("Cansel_Form_Title_Student")}</option>
                                <option value="Skilled Trades Person">
                                    {translate("Cansel_Form_Title_SkilledTradesPerson")}
                                </option>
                                <option value="Other">{translate("Cansel_Form_Title_Other")}</option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={phoneError}
                                id={phoneId}
                                label={phoneConstants.label()}
                                maxLength={phoneConstants.maxLength}
                                name={phoneId}
                                onChange={onChangePhone}
                                placeholder={phoneConstants.placeholder()}
                                required={true}
                                value={phone}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={streetError}
                                id={streetId}
                                label={streetConstants.label()}
                                maxLength={60}
                                name={streetId}
                                onChange={onChangeStreet}
                                placeholder={streetConstants.placeholder()}
                                required={true}
                                value={street}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={cityError}
                                id={cityId}
                                label={cityConstants.label()}
                                maxLength={40}
                                name={cityId}
                                onChange={onChangeCity}
                                placeholder={cityConstants.placeholder()}
                                required={true}
                                value={city}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Select
                                error={stateError}
                                id={stateId}
                                label={stateLabel}
                                name={stateId}
                                onChange={onChangeState}
                                required={true}
                                value={state}
                            >
                                <option value="">{statePlaceholder}</option>
                                {states?.map(state => {
                                    return (
                                        <option key={state.abbreviation} value={state.abbreviation}>
                                            {state.name}
                                        </option>
                                    );
                                })}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                error={postalCodeError}
                                id={zipId}
                                label={postalCodeConstants.label()}
                                maxLength={postalCodeConstants.maxLength}
                                name={zipId}
                                onChange={onChangePostalCode}
                                placeholder={postalCodeConstants.placeholder()}
                                required={true}
                                value={postalCode}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <CheckboxFieldset
                                {...styles.checkboxFieldset}
                                label={translate("Cansel_Form_IndustryInterests")}
                            >
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000Vup7" value="1">
                                    {translate("Cansel_Form_IndustryInterests_Engineering")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000Vuqj" value="1">
                                    {translate("Cansel_Form_IndustryInterests_LandSurveying")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VusL" value="1">
                                    {translate("Cansel_Form_IndustryInterests_ConstructionCivilHeavy")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VuvZ" value="1">
                                    {translate("Cansel_Form_IndustryInterests_Mining")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VufS" value="1">
                                    {translate("Cansel_Form_IndustryInterests_Utilities")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VuxB" value="1">
                                    {translate("Cansel_Form_IndustryInterests_Energy/Oil/Gas")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VuHG" value="1">
                                    {translate("Cansel_Form_IndustryInterests_Forensics")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000W4oU" value="1">
                                    {translate("Cansel_Form_IndustryInterests_Forestry")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NE0000006IXhO" value="1">
                                    {translate("Cansel_Form_IndustryInterests_BuildingConstruction")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00N2S000007RfIQ" value="1">
                                    {translate("Cansel_Form_IndustryInterests_Other")}
                                </CheckboxWithInput>
                            </CheckboxFieldset>
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <CheckboxFieldset
                                {...styles.checkboxFieldset}
                                label={translate("Cansel_Form_ProductInterests")}
                            >
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000Vudq" value="1">
                                    {translate("Cansel_Form_ProductInterests_WideFormatPrinters/Paper/Ink")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000Vu9C" value="1">
                                    {translate("Cansel_Form_ProductInterests_3dCapture/PositioningTechnologies")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VvGX" value="1">
                                    {translate(
                                        "Cansel_Form_ProductInterests_SoftwareAndAssetLifecycleManagementSolutions",
                                    )}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VvI9" value="1">
                                    {translate("Cansel_Form_ProductInterests_FieldsSupplies/FlappingTape/Paint")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VtjO" value="1">
                                    {translate("Cansel_Form_ProductInterests_EarthObservation")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VvJl" value="1">
                                    {translate("Cansel_Form_ProductInterests_SubsurfaceLocators/Inspection")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000VvLN" value="1">
                                    {translate("Cansel_Form_ProductInterests_Lasers/Rangefinders")}
                                </CheckboxWithInput>
                            </CheckboxFieldset>
                        </GridItem>
                        {/* Temporarily disabled
                        <GridItem {...styles.gridItemFull}>
                            <CheckboxFieldset {...styles.checkboxFieldset} label={translate("Cansel_Form_Department")}>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000X1Jt" value="1">
                                    {translate("Cansel_Form_Department_Administration/Accounting")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000X1N7" value="1">
                                    {translate("Cansel_Form_Department_Buying/Rental/Procurement")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000X1Oj" value="1">
                                    {translate("Cansel_Form_Department_HumanResources")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000X1QL" value="1">
                                    {translate("Cansel_Form_Department_InformationTechnology")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000X1Rx" value="1">
                                    {translate("Cansel_Form_Department_Operations")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000X1TZ" value="1">
                                    {translate("Cansel_Form_Department_Renewals/Warranties/Subscriptions")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000X1VB" value="1">
                                    {translate("Cansel_Form_Department_Marketing/Communication/PublicRelations")}
                                </CheckboxWithInput>
                                <CheckboxWithInput {...styles.checkbox} id="00NSv000000X1Wn" value="1">
                                    {translate("Cansel_Form_Department_ServiceRepairs/Logistics/Maintenance")}
                                </CheckboxWithInput>
                            </CheckboxFieldset>
                        </GridItem>
                        */}
                        <GridItem {...styles.gridItemFull}>
                            <TextArea
                                id={commentId}
                                label={translate("Cansel_Form_Comment")}
                                maxLength={255}
                                name={commentId}
                                onChange={onChangeComment}
                                value={comment}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <Typography {...styles.agreementMessage}>
                                {siteMessage("Cansel_ContactUs_Agreement")}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                    <ReCaptcha recaptchaRef={recaptchaRef} location={recaptchaLocation} />
                    <FormActions>
                        <Button {...styles.submitButton} type="submit">
                            {translate("Submit")}
                        </Button>
                    </FormActions>
                </form>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContactUsForm,
    definition: {
        group: "Contact Us",
        displayName: "Salesforce Contact Us Form",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.firstNameId,
                displayName: "First Name ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.firstName),
            },
            {
                name: fields.lastNameId,
                displayName: "Last Name ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.lastName),
            },
            {
                name: fields.emailId,
                displayName: "Email ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.email),
            },
            {
                name: fields.companyId,
                displayName: "Company ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.company),
            },
            {
                name: fields.titleId,
                displayName: "Title ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.title),
            },
            {
                name: fields.phoneId,
                displayName: "Phone ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.phone),
            },
            {
                name: fields.streetId,
                displayName: "Street ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.street),
            },
            {
                name: fields.cityId,
                displayName: "City ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.city),
            },
            {
                name: fields.stateId,
                displayName: "State/Province ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.state),
            },
            {
                name: fields.zipId,
                displayName: "Zip/Postal Code ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.zip),
            },
            {
                name: fields.commentId,
                displayName: "Comment ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip: getFieldIdTooltip(defaultFieldIds.comment),
            },
        ],
    },
};

export default widgetModule;
